import { Field, Formik, useFormik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaBoxes, FaBoxOpen, FaBuilding, FaChartLine, FaCube, FaEdit, FaEuroSign, FaEye, FaFileExcel, FaPlus, FaSave, FaSearch, FaTag, FaTimes, FaTrash, FaUser } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Table, ListGroup, ListGroupItem, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, CardHeader, InputGroup, Pagination, PaginationItem, PaginationLink, FormFeedback, Spinner, Badge } from 'reactstrap';
import Swal from 'sweetalert2';
import { stockActions } from '../../sagas/stockSlice';
import { supplierActions } from '../../sagas/supplierSlice';
import { productActions } from '../../sagas/productSlice';
import { debounce, difference } from 'lodash';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';


const StockPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const { products, loading } = useSelector(state => state.products);
    const [searchQuery, setSearchQuery] = useState('');
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const searchInputRef = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [products]);


    const debouncedSearch = useCallback(
        debounce((query) => {
            setIsSearching(true);

            dispatch(productActions.findAll({
                    search: query,
                    page: 0,
                    size: 10,
                  }));
        }, 1000), // Wait 500ms after last keystroke
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };


    useEffect(() => {
        dispatch(productActions.findAll({
                search: searchQuery,
                page: 0,
                size: 10,
              }));
    }, []);

    const handleOpenModal = (product) => {
        setSelectedProduct(product);
        setModalOpen(true);
    };

    const handleStockAdjustment = (values, { setSubmitting }) => {
        const payload = {
            productId: selectedProduct.id,
            oldQuantity: selectedProduct.stockPhysique,
            newQuantity: values.newQuantity,
            difference: values.difference,
            reason: values.reason,
        };

        dispatch(stockActions.adjustStock({
            data: payload,
            onSuccess: () => {
                Swal.fire({
                    icon: 'success',
                    title: t('stock.adjustmentSuccess'),
                    confirmButtonText: 'OK',
                });
                setModalOpen(false);
                dispatch(productActions.findAll(""));
            },
            onError: () => {
                Swal.fire({
                    icon: 'error',
                    title: t('stock.adjustmentError'),
                    confirmButtonText: 'OK',
                });
            },
        }));

        setSubmitting(false);
    };

    
    return (
        <Container fluid>
            <Row>
                <Col md={12}>
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="mb-0 d-flex align-items-center">
                                    <FaBoxes className="text-primary me-2" />
                                    {t('stock.pageTitle')}
                                </h4>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                    <div className="mt-2 text-muted">
                                        {t('common.loadingData')}
                                    </div>
                                </div>
                            ) : (<>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    {/* Search Input - Left Side */}
                                    <div className="position-relative flex-grow-1 me-3">
                                        <Input
                                            innerRef={searchInputRef}
                                            type="text"
                                            placeholder={t('product.searchFocusHint')}
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                                            title={t('product.searchShortcut')}
                                        />
                                        <FaSearch
                                            style={{
                                                position: 'absolute',
                                                left: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#adb5bd'
                                            }}
                                            size={14}
                                        />
                                        <FaBarcode
                                            style={{
                                                position: 'absolute',
                                                right: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#adb5bd'
                                            }}
                                            size={20}
                                            title={t('product.scanHint')}
                                        />
                                    </div>

                                </div>
                                <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    <Table bordered hover>
                                        <thead>
                                            <tr>
                                                {/* <th>{t('product.lastUpdated')}</th> */}
                                                <th>{t('product.code')}</th>
                                                <th>{t('product.description')}</th>
                                                <th>{t('product.stockPhysique')}</th>
                                                <th>{t('product.stockReserve')}</th>
                                                <th>{t('product.stockDisponible')}</th>
                                                <th>{t('product.stockMinimum')}</th>
                                                <th>{t('common.actions')}</th>

                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!products || products.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {t('product.noProducts')}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (products.map((product) => (
                                                <tr key={product.id}
                                                >
                                                    {/* <td>{product.updatedAt}</td> */}
                                                    <td>{product.code}</td>
                                                    <td>{product.libelle}</td>
                                                    <td>{product.stockPhysique}</td>
                                                    <td>{product.stockReserve}</td>
                                                    <td>{product.stockDisponible}</td>
                                                    <td>{product.stockMinimal}</td>
                                                    <td>
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            onClick={() => handleOpenModal(product)}
                                                        >
                                                            {t('stock.adjust')}
                                                        </Button>
                                                    </td>

                                                </tr>
                                            ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                            </>)}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {/* Modal d'ajustement */}
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
                <ModalHeader toggle={() => setModalOpen(false)}>
                    {t('stock.adjustmentModalTitle')}: {selectedProduct?.libelle}
                </ModalHeader>
                <Formik
                    initialValues={{
                        oldQuantity: selectedProduct?.stockPhysique || 0,
                        newQuantity: selectedProduct?.stockPhysique || 0,
                        difference: 0,
                        reason: 'inventaire',
                    }}
                    enableReinitialize
                    onSubmit={handleStockAdjustment}
                >
                    {({ values, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
                        <Form>
                            <ModalBody>
                                <FormGroup>
                                    <Label>{t('product.code')}</Label>
                                    <Input type="text" value={selectedProduct?.code} disabled />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t('stock.currentStock')}</Label>
                                    <Input type="number" value={values?.oldQuantity} disabled />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t('stock.physicalQuantity')}</Label>
                                    <Field
                                        type="number"
                                        name="newQuantity"
                                        as={Input}
                                        min="0"
                                        onChange={(e) => {
                                            handleChange(e);
                                            const newValue = parseInt(e.target.value, 10) || 0;
                                            const difference = newValue - values.oldQuantity;
                                            setFieldValue('difference', difference);
                                        }}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t('stock.difference')}</Label>
                                    <Input
                                        type="text"
                                        value={values.difference}
                                        disabled
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label>{t('stock.adjustmentReason')}</Label>
                                    <Field
                                        name="reason"
                                        as="select"
                                        className="form-control"
                                        onChange={(e) => {
                                            handleChange(e);
                                            setFieldValue('reason', e.target.value);
                                        }}
                                    >
                                        <option value="inventaire">{t('stock.reasonInventory')}</option>
                                        <option value="casse">{t('stock.reasonBreakage')}</option>
                                        <option value="don">{t('stock.reasonDonation')}</option>
                                        <option value="erreur">{t('stock.reasonError')}</option>
                                    </Field>
                                </FormGroup>
                            </ModalBody>
                            <ModalFooter>
                                <Button color="secondary" onClick={() => setModalOpen(false)}>
                                    {t('common.cancel')}
                                </Button>
                                <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                                    {isSubmitting ? t('common.saving') : t('common.save')}
                                </Button>
                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
            </Modal>
        </Container>
    );
};

export default StockPage;